import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useConfirm } from '../../components/shared/Confirm';
import { useIsMobile } from '../../utils/useIsMobile';

const STATUS_COLORS = { active: 'active', suspended: 'inactive', blocked: 'cancelled' };

const ESTADOS = {
  active:    { label: 'Activo',     color: '#16a34a' },
  suspended: { label: 'Suspendido', color: '#ef4444' },
  blocked:   { label: 'Bloqueado',  color: '#64748b' },
};

const fmtDinero = v => '$ ' + Number(v || 0).toLocaleString('es-CO');
const fmtNum    = v => Number(v || 0).toLocaleString('es-CO');
const fmtFecha  = f => {
  if (!f) return '—';
  const [y, m, d] = String(f).split('-');
  return d ? `${d}/${m}/${y}` : String(f);
};

const toSlug = str => str.toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export default function BusinessesPage() {
  const [businesses, setBusinesses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);
  const [waState, setWaState] = useState(null);   // estado de conexión Evolution
  const [waQr, setWaQr] = useState(null);         // imagen QR base64
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState('');
  const [waInfo, setWaInfo] = useState(null);     // { provider, instance, phone }
  const confirmar = useConfirm();

  // ── Listado del panel: indicadores, filtros y paginacion ──────────────────
  const esMovil = useIsMobile();
  const navigate = useNavigate();
  const [resumen, setResumen]   = useState(null);
  const [ciudades, setCiudades] = useState([]);
  const [filtros, setFiltros]   = useState({ search: '', estado: '', plan_id: '', ciudad: '', desde: '', hasta: '' });
  const [pagina, setPagina]     = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  const [totalPag, setTotalPag] = useState(1);
  const [totalNeg, setTotalNeg] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pagina, por_pagina: porPagina };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });

      const [lista, res, filtrosRes, planesRes] = await Promise.all([
        api.get('/businesses/admin/list', { params }),
        api.get('/businesses/admin/summary'),
        api.get('/businesses/admin/filters'),
        api.get('/plans'),
      ]);
      setBusinesses(lista.data.negocios || []);
      setTotalPag(lista.data.paginas || 1);
      setTotalNeg(lista.data.total || 0);
      setResumen(res.data);
      setCiudades(filtrosRes.data.ciudades || []);
      setPlans(planesRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error cargando negocios');
      setTimeout(() => setError(''), 5000);
    } finally { setLoading(false); }
  }, [pagina, porPagina, filtros]);

  useEffect(() => { load(); }, [load]);

  const cambiarFiltro = (campo, valor) => {
    setFiltros(f => ({ ...f, [campo]: valor }));
    setPagina(1); // cualquier filtro nuevo vuelve a la primera pagina
  };

  const limpiarFiltros = () => {
    setFiltros({ search: '', estado: '', plan_id: '', ciudad: '', desde: '', hasta: '' });
    setPagina(1);
  };

  // Exporta lo que se ve en pantalla a un CSV que Excel abre correctamente
  const exportar = () => {
    const cab = ['Negocio', 'Propietario', 'Email', 'Plan', 'Estado', 'Ciudad', 'Registro', 'Usuarios', 'Ingresos del mes'];
    const filas = businesses.map(b => [
      b.nombre, b.propietario || '', b.propietario_email || '', b.plan || '',
      ESTADOS[b.estado]?.label || b.estado, b.ciudad || '',
      fmtFecha(b.creado_en), b.usuarios, b.ingresos_mes,
    ]);
    const csv = [cab, ...filas]
      .map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `negocios-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const EMPTY = { name: '', slug: '', legal_name: '', nit: '', address: '', city: '', department: '', phone: '', email: '', plan_id: '', negotiation_type: 'plaza', negotiation_value: '' };

  const openCreate = () => { setForm(EMPTY); setModal('create'); setError(''); };
  const openEdit = async (fila) => {
    // El listado del panel trae solo las columnas de la tabla; el formulario
    // necesita el registro COMPLETO (razon social, NIT, direccion, plan,
    // credenciales de WhatsApp...). Por eso se pide antes de abrir el modal:
    // sin esto los campos saldrian vacios y al guardar se borrarian.
    let b = fila;
    try {
      const r = await api.get(`/businesses/${fila.id}`);
      b = { ...fila, ...r.data };
    } catch {
      setError('No se pudieron cargar todos los datos del negocio');
      setTimeout(() => setError(''), 5000);
    }

    // El API devuelve las columnas en español (nombre, direccion, ciudad...).
    // Aquí se traducen a las claves que usan el formulario y el PUT.
    setForm({
      ...b,
      name:              b.nombre || b.name || '',
      slug:              b.slug || '',
      legal_name:        b.razon_social || b.legal_name || '',
      nit:               b.nit || '',
      address:           b.direccion || b.address || '',
      city:              b.ciudad || b.city || '',
      department:        b.departamento || b.department || '',
      phone:             b.telefono || b.phone || '',
      email:             b.email || '',
      website:           b.sitio_web || b.website || '',
      negotiation_type:  b.tipo_negociacion || b.negotiation_type || 'plaza',
      negotiation_value: b.valor_negociacion ?? b.negotiation_value ?? '',
      logo_url:          b.url_logo || b.logo_url || '',
      plan_id:           b.plan_id || '',
      whatsapp_provider: b.whatsapp_provider || 'evolution',
    });
    setModal(b);
    setError('');
    setWaQr(null); setWaMsg(''); setWaInfo(null);
    setWaState(null);
    api.get(`/whatsapp/${b.id}/status`)
      .then(r => { setWaState(r.data.state); setWaInfo(r.data); })
      .catch(() => setWaState('no_disponible'));
  };

  const waVincular = async (businessId) => {
    setWaBusy(true); setWaMsg(''); setWaQr(null);
    try {
      await api.post(`/whatsapp/${businessId}/instance`);
      const qrRes = await api.get(`/whatsapp/${businessId}/qr`);
      if (qrRes.data.qr) {
        setWaQr(qrRes.data.qr);
        setWaMsg('Escanea el QR con el WhatsApp del negocio (Dispositivos vinculados). El QR caduca en ~40 segundos.');
      } else {
        setWaMsg('No hay QR pendiente — la instancia parece estar ya conectada. Usa "Verificar conexión".');
      }
    } catch (err) {
      setWaMsg(err.response?.data?.error || 'Error comunicándose con Evolution API');
    } finally { setWaBusy(false); }
  };

  const waVerificar = async (businessId) => {
    setWaBusy(true);
    try {
      const r = await api.get(`/whatsapp/${businessId}/status`);
      setWaState(r.data.state); setWaInfo(r.data);
      if (r.data.state === 'open') { setWaQr(null); setWaMsg('✅ WhatsApp conectado.'); }
      else setWaMsg(`Estado actual: ${r.data.state}`);
    } catch { setWaMsg('No se pudo consultar el estado'); }
    finally { setWaBusy(false); }
  };

  const waDesvincular = async (businessId) => {
    if (!await confirmar({
      titulo: 'Cerrar sesión de WhatsApp',
      mensaje: '¿Cerrar la sesión de WhatsApp de este negocio?',
      detalle: 'Dejará de enviar mensajes hasta que se vincule de nuevo escaneando el QR.',
      aceptar: 'Cerrar sesión',
      peligro: true,
    })) return;
    setWaBusy(true);
    try {
      await api.delete(`/whatsapp/${businessId}/session`);
      setWaState('close'); setWaQr(null); setWaMsg('Sesión cerrada.');
    } catch (err) { setWaMsg(err.response?.data?.error || 'No se pudo cerrar la sesión'); }
    finally { setWaBusy(false); }
  };

  const handleNameChange = val => {
    setForm(f => ({ ...f, name: val, slug: f.slug ? f.slug : toSlug(val) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') {
        await api.post('/businesses', form);
      } else {
        await api.put(`/businesses/${modal.id}`, form);
        await api.patch(`/businesses/${modal.id}/whatsapp`, {
          whatsapp_provider: form.whatsapp_provider || 'evolution',
          ultramsg_instance_id: form.ultramsg_instance_id || '',
          ultramsg_token: form.ultramsg_token || '',
        });
        if (form.plan_id) {
          const planRes = await api.patch(`/businesses/${modal.id}/plan`, { plan_id: form.plan_id });
          const msg = planRes.data?.message || '';
          if (msg.toLowerCase().includes('sucursal')) {
            // El backend informó que se inactivaron sucursales
            setTimeout(() => alert(msg), 100);
          }
        }
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/businesses/${id}/status`, { status });
    load();
  };

  const copyLink = (slug, id) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Negocios</h2>
          <p>Administra todos los negocios registrados en la plataforma</p>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* ── Tarjetas de indicadores ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '1rem', marginBottom: '1.25rem',
      }}>
        {[
          { label: 'Total negocios', valor: resumen?.total,       icono: '🏪', color: '#6366f1' },
          { label: 'Activos',        valor: resumen?.activos,     icono: '✅', color: '#16a34a' },
          { label: 'Suspendidos',    valor: resumen?.suspendidos, icono: '⛔', color: '#ef4444' },
          { label: 'Suscripción vencida', valor: resumen?.vencidos, icono: '⏰', color: '#f59e0b' },
          { label: 'Nuevos este mes', valor: resumen?.nuevos_mes, icono: '➕', color: '#2563eb',
            variacion: resumen?.variacion_nuevos },
        ].map(k => (
          <div key={k.label} style={{ background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '1rem 1.1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: k.color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{k.icono}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', lineHeight: 1.2 }}>{k.label}</div>
            </div>
            <div style={{ fontSize: esMovil ? '1.25rem' : '1.5rem', fontWeight: 800 }}>{fmtNum(k.valor)}</div>
            {/* La variacion solo se muestra cuando hay mes anterior con datos */}
            {k.variacion !== undefined && k.variacion !== null && (
              <div style={{ fontSize: '0.75rem', marginTop: 3, color: k.variacion >= 0 ? '#16a34a' : '#ef4444' }}>
                {k.variacion >= 0 ? '↑' : '↓'} {Math.abs(k.variacion)}% vs mes anterior
              </div>
            )}
            <div style={{ height: 3, borderRadius: 99, background: k.color, opacity: 0.25, marginTop: 10 }} />
          </div>
        ))}
      </div>

      {/* ── Filtros ── */}
      <div style={{ background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '1rem 1.1rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
          <input className="form-input" placeholder="Buscar negocio, email o ciudad..."
            value={filtros.search} onChange={e => cambiarFiltro('search', e.target.value)} />

          <select className="form-select" value={filtros.estado} onChange={e => cambiarFiltro('estado', e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="suspended">Suspendido</option>
            <option value="blocked">Bloqueado</option>
          </select>

          <select className="form-select" value={filtros.plan_id} onChange={e => cambiarFiltro('plan_id', e.target.value)}>
            <option value="">Todos los planes</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
          </select>

          <select className="form-select" value={filtros.ciudad} onChange={e => cambiarFiltro('ciudad', e.target.value)}>
            <option value="">Todas las ciudades</option>
            {ciudades.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <input type="date" className="form-input" title="Registrados desde"
            value={filtros.desde} onChange={e => cambiarFiltro('desde', e.target.value)} />
          <input type="date" className="form-input" title="Registrados hasta"
            value={filtros.hasta} onChange={e => cambiarFiltro('hasta', e.target.value)} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
          <button className="btn btn-ghost" style={{ fontSize: '0.82rem' }} onClick={limpiarFiltros}>✕ Limpiar filtros</button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" style={{ fontSize: '0.82rem' }} onClick={exportar}>⬇ Exportar</button>
            <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} onClick={openCreate}>+ Nuevo negocio</button>
            <button className="btn btn-secondary" style={{ fontSize: '0.82rem' }}
                    onClick={() => navigate('/superadmin/permissions')}>🌳 Permisos</button>
          </div>
        </div>
      </div>

      {/* ── Listado ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : businesses.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          No hay negocios que coincidan con los filtros
        </div>
      ) : esMovil ? (
        // En celular cada negocio se muestra como tarjeta
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {businesses.map(b => (
            <div key={b.id} className="card" style={{ padding: '0.9rem' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                {b.url_logo
                  ? <img src={b.url_logo} alt="" style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 38, height: 38, borderRadius: 9, background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>🏪</div>}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{b.nombre}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>NEG-{String(b.id).padStart(4, '0')}</div>
                </div>
              </div>
              <div style={{ fontSize: '0.82rem', marginBottom: 3 }}>{b.propietario || 'Sin propietario'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 3 }}>
                <span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{b.plan || 'Sin plan'}</span>
                <span style={{ color: ESTADOS[b.estado]?.color, fontWeight: 600 }}>{ESTADOS[b.estado]?.label || b.estado}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                <span>{b.ciudad || '—'} · {fmtFecha(b.creado_en)}</span>
                <strong style={{ color: 'var(--color-gold)' }}>{fmtDinero(b.ingresos_mes)}</strong>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button className="btn btn-secondary" style={{ flex: 1, fontSize: '0.8rem' }} onClick={() => openEdit(b)}>✏️ Editar</button>
                <button className="btn btn-ghost" style={{ flex: 1, fontSize: '0.8rem' }} onClick={() => copyLink(b.slug, b.id)}>
                  {copied === b.id ? '✓ Copiado' : '🔗 Link'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Negocio</th><th>Propietario</th><th>Plan</th><th>Estado</th>
                  <th>Ciudad</th><th>Registro</th><th>Usuarios</th><th>Ingresos (mes)</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {b.url_logo
                          ? <img src={b.url_logo} alt="" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>🏪</div>}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{b.nombre}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                            NEG-{String(b.id).padStart(4, '0')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.84rem' }}>
                      {b.propietario || '—'}
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{b.propietario_email || ''}</div>
                    </td>
                    <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{b.plan || '—'}</span></td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: ESTADOS[b.estado]?.color }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ESTADOS[b.estado]?.color || '#888' }} />
                        {ESTADOS[b.estado]?.label || b.estado}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.84rem' }}>{b.ciudad || '—'}</td>
                    <td style={{ fontSize: '0.84rem', whiteSpace: 'nowrap' }}>{fmtFecha(b.creado_en)}</td>
                    <td style={{ fontSize: '0.84rem' }}>{b.usuarios}</td>
                    <td style={{ fontWeight: 600, color: 'var(--color-gold)', whiteSpace: 'nowrap' }}>{fmtDinero(b.ingresos_mes)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-ghost" title="Copiar link de agendamiento"
                          onClick={() => copyLink(b.slug, b.id)}>{copied === b.id ? '✓' : '🔗'}</button>
                        <button className="btn btn-sm btn-secondary" title="Editar" onClick={() => openEdit(b)}>✏️</button>
                        {b.estado === 'active'
                          ? <button className="btn btn-sm btn-danger" title="Suspender" onClick={() => changeStatus(b.id, 'suspended')}>⛔</button>
                          : <button className="btn btn-sm btn-secondary" title="Activar" onClick={() => changeStatus(b.id, 'active')}>✅</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Paginacion ── */}
      {!loading && totalNeg > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
          justifyContent: 'space-between', marginTop: '1rem',
          fontSize: '0.84rem', color: 'var(--color-text-muted)',
        }}>
          <span>
            Mostrando {(pagina - 1) * porPagina + 1} a {Math.min(pagina * porPagina, totalNeg)} de {fmtNum(totalNeg)} negocios
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" style={{ padding: '4px 10px' }}
              disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>‹</button>
            <span style={{ padding: '0 6px' }}>Página <strong>{pagina}</strong> de {totalPag}</span>
            <button className="btn btn-ghost" style={{ padding: '4px 10px' }}
              disabled={pagina >= totalPag} onClick={() => setPagina(p => p + 1)}>›</button>
            <select className="form-select" style={{ padding: '4px 8px', fontSize: '0.8rem', width: 'auto' }}
              value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / página</option>)}
            </select>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 660 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nuevo Negocio' : 'Editar Negocio'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
                <div className="form-grid">
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Nombre comercial *</label>
                    <input
                      className="form-input"
                      required
                      value={form.name || ''}
                      onChange={e => handleNameChange(e.target.value)}
                    />
                  </div>

                  {/* Slug / Link único */}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">
                      Link único de agendamiento *
                      <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.78rem', marginLeft: 8 }}>
                        (solo letras, números y guiones)
                      </span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        /book/
                      </span>
                      <input
                        className="form-input"
                        required
                        placeholder="mi-barberia"
                        value={form.slug || ''}
                        onChange={e => setForm(f => ({ ...f, slug: toSlug(e.target.value) }))}
                        style={{ flex: 1 }}
                      />
                    </div>
                    {form.slug && (
                      <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--color-gold)' }}>
                        Link: <strong>{window.location.origin}/book/{form.slug}</strong>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Razón social</label>
                    <input className="form-input" value={form.legal_name ?? ''} onChange={e => setForm(f => ({ ...f, legal_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">NIT</label>
                    <input className="form-input" value={form.nit || ''} onChange={e => setForm(f => ({ ...f, nit: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Dirección</label>
                    <input className="form-input" value={form.address ?? ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" required value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Plan {modal === 'create' ? '*' : ''}</label>
                    <select className="form-select" value={form.plan_id || ''} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
                      <option value="">Sin plan</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Modelo de negociación</label>
                    <select className="form-select" value={form.negotiation_type || 'plaza'} onChange={e => setForm(f => ({ ...f, negotiation_type: e.target.value }))}>
                      <option value="plaza">Por plaza</option>
                      <option value="percentage">Por porcentaje</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor / %</label>
                    <input type="number" className="form-input" value={form.negotiation_value ?? ''} onChange={e => setForm(f => ({ ...f, negotiation_value: e.target.value }))} />
                  </div>

                  {modal !== 'create' && (
                    <>
                      <div style={{ gridColumn: '1/-1', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                        📱 WhatsApp del negocio — los mensajes de confirmación salen del número vinculado aquí
                      </div>

                      <div className="form-group">
                        <label className="form-label">Proveedor</label>
                        <select
                          className="form-select"
                          value={form.whatsapp_provider || 'evolution'}
                          onChange={e => { setForm(f => ({ ...f, whatsapp_provider: e.target.value })); setWaQr(null); setWaMsg('Guarda los cambios para aplicar el proveedor y luego vincula.'); }}
                        >
                          <option value="evolution">Evolution API (servidor propio)</option>
                          <option value="ultramsg">UltraMsg (servicio pago por número)</option>
                        </select>
                      </div>
                      <div className="form-group" />

                      {form.whatsapp_provider === 'ultramsg' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">UltraMsg Instance ID</label>
                            <input className="form-input" placeholder="instance123456" value={form.ultramsg_instance_id || ''} onChange={e => setForm(f => ({ ...f, ultramsg_instance_id: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">UltraMsg Token</label>
                            <input className="form-input" placeholder="token de la instancia" value={form.ultramsg_token || ''} onChange={e => setForm(f => ({ ...f, ultramsg_token: e.target.value }))} />
                          </div>
                          <div style={{ gridColumn: '1/-1', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: -4, marginBottom: 4 }}>
                            Crea la instancia en ultramsg.com, copia aquí el Instance ID y el Token, pulsa <strong>Guardar</strong> y luego vincula el número con el QR.
                          </div>
                        </>
                      )}

                      <div style={{ gridColumn: '1/-1' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                          <span className={`badge badge-${waState === 'open' ? 'success' : waState === 'connecting' ? 'warning' : 'secondary'}`}>
                            {waState === 'open' ? '✅ Conectado'
                              : waState === 'connecting' ? '🔄 Conectando'
                              : waState === 'close' ? '🔌 Desconectado'
                              : waState === 'sin_instancia' ? 'Sin vincular'
                              : waState === 'no_disponible' ? 'Servidor Evolution no disponible'
                              : waState || 'Consultando...'}
                          </span>
                          {waInfo?.instance && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                              instancia: {waInfo.instance}{waInfo.phone ? ` · número: ${waInfo.phone}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-primary" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waVincular(modal.id)}>
                            {waBusy ? '...' : '📷 Vincular WhatsApp (QR)'}
                          </button>
                          <button type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waVerificar(modal.id)}>
                            Verificar conexión
                          </button>
                          {waState === 'open' && (
                            <button type="button" className="btn btn-ghost" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waDesvincular(modal.id)}>
                              Desvincular
                            </button>
                          )}
                        </div>
                        {waMsg && <div style={{ fontSize: '0.8rem', marginTop: 8, color: 'var(--color-text-muted)' }}>{waMsg}</div>}
                        {waQr && (
                          <div style={{ marginTop: 10, textAlign: 'center' }}>
                            <img src={waQr} alt="QR para vincular WhatsApp" style={{ width: 220, height: 220, borderRadius: 8, border: '1px solid var(--color-border)', background: '#fff', padding: 6 }} />
                          </div>
                        )}
                      </div>

                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
